import type {
  CreateRecommendationPayload,
  GetRecommendationsPayload,
  GetRecommendationsResponse,
  Recommendation,
  UpdateRecommendationPayload,
} from '../../types/index.js';
import { baseApi } from '../base-api.js';

type RecommendationResponse = { recommendation: Recommendation };

const recommendationListTags = (result: GetRecommendationsResponse | undefined) =>
  result
    ? [
        ...result.recommendations.map(({ id }) => ({ type: 'Recommendation' as const, id })),
        { type: 'Recommendation' as const, id: 'LIST' },
      ]
    : [{ type: 'Recommendation' as const, id: 'LIST' }];

/**
 * Mirrors `apps/server/src/modules/recommendation` (`recommendationController`, prefix
 * `/recommendations`). No `GET /:id` route exists server-side, only list + mutations. Nothing
 * outside this domain embeds recommendation content (unlike `Category`, which is embedded by
 * both `Question` and `Recommendation` — see `category.api.ts`), so mutations here only ever
 * invalidate their own tag, never `Module`/`Category`/`Question`.
 *
 * The list query keeps the server envelope (`{ recommendations, total, page, limit }`) rather
 * than unwrapping to an array: the admin table paginates, so it needs the server-side `total`,
 * which the page slice alone cannot provide.
 */
export const recommendationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getRecommendations: builder.query<GetRecommendationsResponse, GetRecommendationsPayload>({
      query: (payload) => ({ path: ['recommendations'], method: 'get', query: payload }),
      providesTags: (result) => recommendationListTags(result),
    }),

    createRecommendation: builder.mutation<Recommendation, CreateRecommendationPayload>({
      query: (body) => ({ path: ['recommendations'], method: 'post', body }),
      transformResponse: (response: RecommendationResponse) => response.recommendation,
      invalidatesTags: [{ type: 'Recommendation', id: 'LIST' }],
    }),

    updateRecommendation: builder.mutation<Recommendation, { id: string; payload: UpdateRecommendationPayload }>({
      query: ({ id, payload }) => ({
        path: ['recommendations', { param: 'recommendation_id', value: id }],
        method: 'put',
        body: payload,
      }),
      transformResponse: (response: RecommendationResponse) => response.recommendation,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Recommendation', id },
        { type: 'Recommendation', id: 'LIST' },
      ],
    }),

    deleteRecommendation: builder.mutation<void, string>({
      query: (id) => ({ path: ['recommendations', { param: 'recommendation_id', value: id }], method: 'delete' }),
      transformResponse: (): void => undefined,
      invalidatesTags: (_result, _error, id) => [
        { type: 'Recommendation', id },
        { type: 'Recommendation', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetRecommendationsQuery,
  useCreateRecommendationMutation,
  useUpdateRecommendationMutation,
  useDeleteRecommendationMutation,
} = recommendationApi;
